import { SensorTagId } from 'types/models/samplePoint';
import {
  PipeTriggerType,
  TRIGGER_TYPE_TO_UNIT_TYPE,
  TriggerType,
  TriggerTypeWithSingleUnitType,
  TriggerTypeWithUnit,
  ValueType
} from 'types/trigger.enum';
import { UnitType } from 'utils/get-unit-by-country';

// Note: Should sync with the switch cases in `getTriggerUnitType`
export function isMultiUnitTrigger(triggerType: TriggerType): boolean {
  switch (triggerType) {
    case TriggerType.DAM_HIGH:
    case TriggerType.DAM_LOW:
    case TriggerType.FUEL_LOW:
    case TriggerType.LIQUID_FERTILISER_LOW:
    case TriggerType.WATER_TANK_HIGH:
    case TriggerType.WATER_TANK_LOW:
      return true;
    default:
      return false;
  }
}

/** Helper function for triggers that support multiple ValueTypes */
function deriveUnitTypeFromValueType(valueType: ValueType, isDam: boolean) {
  switch (valueType) {
    case ValueType.VOLUME:
      if (isDam) {
        return UnitType.VOLUME_LG;
      }
      return UnitType.VOLUME_SM;
    case ValueType.REDUCED_LEVEL:
      return UnitType.LEVEL_MD;
    case ValueType.VALUE:
    default:
      return UnitType.LEVEL_SM;
  }
}

const getSingleUnitTypeFromTriggerType = (triggerType: TriggerTypeWithSingleUnitType): UnitType => {
  return TRIGGER_TYPE_TO_UNIT_TYPE[triggerType];
};

export default function getTriggerUnitType(
  triggerType: Exclude<TriggerTypeWithUnit, PipeTriggerType>,
  valueType: ValueType
) {
  switch (triggerType) {
    // Multi-unit triggers - use valueType-based logic
    // Note: Should sync with `isMultiUnitTrigger`
    case TriggerType.DAM_HIGH:
    case TriggerType.DAM_LOW:
    case TriggerType.FUEL_LOW:
    case TriggerType.LIQUID_FERTILISER_LOW:
    case TriggerType.WATER_TANK_HIGH:
    case TriggerType.WATER_TANK_LOW:
      return deriveUnitTypeFromValueType(
        valueType,
        triggerType === TriggerType.DAM_HIGH || triggerType === TriggerType.DAM_LOW
      );

    // 1:1 mappings
    default:
      // Use a function to enable type narrowing, i.e. ensure the switch cases above are exhaustive
      return getSingleUnitTypeFromTriggerType(triggerType);
  }
}

export function getPipeTriggerUnitType(
  triggerType: PipeTriggerType,
  sensorTagId: SensorTagId
): UnitType {
  switch (triggerType) {
    case TriggerType.PIPE_FLOW_RATE_HIGH:
    case TriggerType.PIPE_FLOW_RATE_LOW:
      return sensorTagId === SensorTagId.GAS_FLOW_METER ? UnitType.FLOW_GAS : UnitType.FLOW;
    case TriggerType.PIPE_VOLUME_ROLLING_WINDOW_HIGH:
    case TriggerType.PIPE_VOLUME_ROLLING_WINDOW_LOW:
      return sensorTagId === SensorTagId.GAS_FLOW_METER ? UnitType.VOLUME_GAS : UnitType.VOLUME_SM;
    default:
      throw new Error(`Unexpected pipe trigger type: ${triggerType}`);
  }
}